import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Slider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { insiderApi } from '../../services/api';

function AlertSettings() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const defaultConfig = {
    email: '',
    min_confidence: 0.7,
    signal_types: ['STRONG_BUY', 'BUY'],
    enabled: true,
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      const response = await insiderApi.getAlertConfigs();
      // API now returns { configs: [...] } with all alert configs
      const data = response.data;
      if (data && data.configs && Array.isArray(data.configs)) {
        setConfigs(data.configs);
      } else if (data && data.email) {
        // Fallback for legacy single config response
        if (!data.id) {
          data.id = 'default';
        }
        setConfigs([data]);
      } else {
        setConfigs([]);
      }
    } catch (err) {
      console.error('Error loading alert configs:', err);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (config = null) => {
    setEditingConfig(config ? { ...config } : { ...defaultConfig });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConfig(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: editingConfig.enabled,
        email: editingConfig.email,
        min_confidence: editingConfig.min_confidence,
        signal_types: editingConfig.signal_types || ['STRONG_BUY', 'BUY'],
      };

      if (editingConfig.id && editingConfig.id !== 'default') {
        // Update existing config
        await insiderApi.updateAlertConfig(editingConfig.id, payload);
      } else {
        // Create new config
        await insiderApi.createAlertConfig(payload);
      }
      setSnackbar({ open: true, message: 'Alert configuration saved!', severity: 'success' });
      handleCloseDialog();
      loadConfigs();
    } catch (err) {
      console.error('Error saving config:', err);
      setSnackbar({ open: true, message: 'Failed to save configuration.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this alert configuration?')) return;
    try {
      await insiderApi.deleteAlertConfig(id);
      setSnackbar({ open: true, message: 'Configuration deleted.', severity: 'success' });
      loadConfigs();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete configuration.', severity: 'error' });
    }
  };

  const handleSignalTypeToggle = (type) => {
    const types = editingConfig.signal_types || [];
    if (types.includes(type)) {
      setEditingConfig({
        ...editingConfig,
        signal_types: types.filter((t) => t !== type),
      });
    } else {
      setEditingConfig({
        ...editingConfig,
        signal_types: [...types, type],
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Insider Alert Settings
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add Alert
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Min Confidence</TableCell>
                <TableCell>Signal Types</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" py={3}>
                      No alert configurations yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                configs.map((config) => (
                  <TableRow key={config.id}>
                    <TableCell>{config.email}</TableCell>
                    <TableCell>{(config.min_confidence * 100).toFixed(0)}%</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {config.signal_types?.map((type) => (
                          <Chip
                            key={type}
                            label={type}
                            size="small"
                            sx={{
                              bgcolor: type === 'STRONG_BUY' ? '#1b5e20' : '#4caf50',
                              color: 'white',
                            }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={config.enabled ? 'Active' : 'Disabled'}
                        size="small"
                        color={config.enabled ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog(config)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(config.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingConfig?.id ? 'Edit Alert' : 'Add Alert'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={editingConfig?.email || ''}
            onChange={(e) => setEditingConfig({ ...editingConfig, email: e.target.value })}
            sx={{ mt: 2, mb: 3 }}
          />

          <Typography gutterBottom>
            Minimum Confidence: {((editingConfig?.min_confidence || 0.7) * 100).toFixed(0)}%
          </Typography>
          <Slider
            value={editingConfig?.min_confidence || 0.7}
            onChange={(e, value) => setEditingConfig({ ...editingConfig, min_confidence: value })}
            min={0.5}
            max={0.99}
            step={0.01}
            marks={[
              { value: 0.5, label: '50%' },
              { value: 0.75, label: '75%' },
              { value: 0.99, label: '99%' },
            ]}
            sx={{ mb: 3 }}
          />

          <Typography gutterBottom>Signal Types</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            {['STRONG_BUY', 'BUY'].map((type) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    checked={editingConfig?.signal_types?.includes(type) || false}
                    onChange={() => handleSignalTypeToggle(type)}
                  />
                }
                label={type}
              />
            ))}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={editingConfig?.enabled || false}
                onChange={(e) => setEditingConfig({ ...editingConfig, enabled: e.target.checked })}
              />
            }
            label="Enabled"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AlertSettings;
